import { useState } from "react";
import axios from "axios";
import { FileSearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { INTERIM_WRONG_CODE, previewInterimReport, type Report } from "../../../../../api/reports";
import { useIsEdgeLocal } from "../../../../../hooks/useEdgeMode";
import { useAppSelector } from "../../../../../store/hooks";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { RestaurantAccessModal } from "../../../../modules/components/RestaurantAccessModal";
import { useOrderingMessage } from "../../useOrderingMessage";
import type { SecondaryAction } from "../OptionsPopover";
import { PersonalReportPreviewModal } from "./PersonalReportPreviewModal";

// Правото на бутона (org seed) — BE: `INTERIM_PERMISSION` в reports/service_interim.py.
const INTERIM_PERMISSION = "interim_report";

const isWrongCode = (error: unknown) =>
  axios.isAxiosError(error) &&
  (error.response?.data as { detail?: unknown } | undefined)?.detail === INTERIM_WRONG_CODE;

/**
 * „Междинен отчет" в „Опции" на лентата под масите: бутонът, прозорецът за
 * кода (същият като „Тестов режим", със свое заглавие) и прегледът на
 * СОБСТВЕНИЯ персонален отчет 1:1 като „Персонален отчет" → „Преглед", но
 * без футъра (затваря се от X горе вдясно) — нищо не се издава. Кодът решава BE: ПИН-ът за вход с
 * код — без тестовите сметки, кодът за тестов режим — всички. Грешен код —
 * прозорецът остава за нов опит; друга грешка го затваря. Без правото — нищо
 * (null); в локален режим бутонът е заключен (касата няма този път).
 */
export const useInterimReportAction = () => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const permissions = useAppSelector((state) => state.auth.permissions);
  const canInterimReport = permissions.some((permission) => permission.name === INTERIM_PERMISSION);
  const edgeLocal = useIsEdgeLocal();
  const [codeOpen, setCodeOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<Report | null>(null);

  const submitCode = async (code: string) => {
    if (loading) return;
    setLoading(true);
    try {
      const interim = await previewInterimReport(code);
      setCodeOpen(false);
      setReport(interim);
    } catch (error) {
      const wrongCode = isWrongCode(error);
      message.error(
        wrongCode ? t("codeKeypad.invalidCode") : getApiErrorMessage(error, t("generateReport.previewError")),
      );
      if (!wrongCode) setCodeOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const action: SecondaryAction | null = canInterimReport
    ? {
        key: "interimReport",
        label: t("ordering.interimReport"),
        icon: <FileSearchOutlined />,
        onClick: () => setCodeOpen(true),
        lockedTooltip: edgeLocal ? t("edge.unavailableOffline") : undefined,
      }
    : null;

  const modal = canInterimReport ? (
    <>
      <RestaurantAccessModal
        open={codeOpen}
        onClose={() => setCodeOpen(false)}
        onCodeSubmit={submitCode}
        loading={loading}
        title={t("ordering.interimReport")}
        subtitle={t("ordering.interimReportCode")}
      />
      <PersonalReportPreviewModal
        open={report !== null}
        onClose={() => setReport(null)}
        report={report}
        hideFooter
      />
    </>
  ) : null;

  return { action, modal, open: codeOpen || report !== null };
};
